import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { MapPin, Download } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Hero = ({ data }) => {
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleDownloadResume = async () => {
    try {
      const response = await axios.get(`${API}/resume/download`, {
        responseType: 'blob'
      });

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Sadasiva_Velkuri_Resume.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast({
        title: "Resume Downloaded!",
        description: "Thank you for your interest.",
      });
    } catch (error) {
      console.error('Error downloading resume:', error);
      toast({
        title: "Download Failed",
        description: "Failed to download resume. Please try again.",
        variant: "destructive"
      });
    }
  };

  const navigateToProjects = () => {
    navigate('/projects');
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#0a0a0a]">
      {/* Background overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0a0a0a]/80 to-[#0a0a0a] z-10"></div>
      
      {/* Background Image */}
      <div 
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1664526936810-ec0856d31b92)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'grayscale(100%)'
        }}
      ></div>

      <div className="relative z-20 max-w-6xl mx-auto px-6 py-20 text-center">
        {/* Availability Badge */}
        <div className="flex justify-center mb-8 animate-fade-in">
          <Badge 
            variant="outline" 
            className="bg-[#1a1a1a] border-[#2a2a2a] text-white px-4 py-2 text-sm font-normal"
          >
            <span className="inline-block w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>
            {data.personalInfo.availability}
          </Badge>
        </div>

        {/* Main Heading */}
        <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
          Hi, I'm {data.personalInfo.name.split(' ')[0]}.
          <br />
          <span className="text-[#a3a3a3]">{data.personalInfo.title}</span>
        </h1>

        {/* Tagline */}
        <p className="text-xl md:text-2xl text-[#a3a3a3] max-w-3xl mx-auto mb-10 leading-relaxed">
          {data.personalInfo.tagline}
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
          <Button 
            onClick={navigateToProjects}
            size="lg"
            className="bg-white text-black hover:bg-[#e5e5e5] border-2 border-white transition-all duration-300 text-base font-semibold px-8 py-6"
          >
            View My Work
          </Button>
          <Button 
            onClick={handleDownloadResume}
            size="lg"
            variant="outline"
            className="bg-transparent text-white border-2 border-white hover:bg-white hover:text-black transition-all duration-300 text-base font-semibold px-8 py-6"
          >
            <Download className="mr-2 h-5 w-5" />
            Download Resume
          </Button>
        </div>

        {/* Location */}
        <div className="flex items-center justify-center text-[#a3a3a3] text-sm">
          <MapPin className="h-4 w-4 mr-2" />
          <span>{data.personalInfo.location}</span>
        </div>
      </div>
    </section>
  );
};

export default Hero;
